import { useState, useEffect, useCallback } from "react";
import { getSubContractors } from "../services/subContractorService";

interface ApiError {
  message?: string;
}

export interface SubContractor {
  id: string;
  companyName: string;
  email: string;
  phone: string;
  [key: string]: unknown;
}

interface Pagination {
  total?: number;
  totalPages?: number;
}

interface SubContractorParams {
  page?: number;
  limit?: number;
}

const useGetSubContractors = ({ page = 1, limit = 20 }: SubContractorParams = {}) => {
  const [subContractors, setSubContractors] = useState<SubContractor[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSubContractors = useCallback(async () => {
    setLoading(true);
    setError("");

    const { data, error: apiError } = await getSubContractors({ page, limit }) as {
      data: { data?: SubContractor[]; pagination?: Pagination } | null;
      error: ApiError | null;
    };

    setLoading(false);

    if (apiError) {
      setError(apiError.message || "Failed to load sub-contractors.");
      return;
    }

    const list = data?.data ?? [];
    const pag = data?.pagination ?? {};
    setSubContractors(Array.isArray(list) ? list : []);
    setTotal(pag.total ?? 0);
    setTotalPages(pag.totalPages ?? Math.ceil((pag.total ?? 0) / limit));
  }, [page, limit]);

  useEffect(() => {
    fetchSubContractors();
  }, [fetchSubContractors]);

  return { subContractors, total, totalPages, loading, error, refetch: fetchSubContractors };
};

export default useGetSubContractors;
